import React from "react";
import Helmet from "react-helmet";
import { useParams, useHistory } from "react-router-dom";

import { LoadingSpinner } from "components/ProgressIndicator";

import useDeskpassRepository from "hooks/useRepository/deskpass";

import SessionContext from "context/session";
import NotificationContext from "context/notification";

import routes from "lib/routes";

import { DeskpassError } from "repository/deskpass/types/meta";

type RouteParams = {
  hash: string;
};

const ConfirmEmailPage = () => {
  const { hash } = useParams<RouteParams>();
  const { replace: replaceRoute } = useHistory();
  const { loadSelf } = React.useContext(SessionContext);
  const { addNotififcation } = React.useContext(NotificationContext);

  const [, verifySignupEmail] = useDeskpassRepository(
    (repository) => (verificationHash: string) =>
      repository.signupEmailVerification.verifySignupEmail(verificationHash),
    { fireOnMount: false }
  );

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await verifySignupEmail(hash);

        try {
          await loadSelf();
        } catch {
          // No session (e.g. opened link in another browser) — routing guards handle it.
        }

        if (!cancelled) {
          replaceRoute(routes.reservation);
        }
      } catch (err) {
        const errorCode = (err as DeskpassError)?.error;
        const message =
          errorCode === "dataNotFound" || errorCode === "dataExpired"
            ? "This verification link is invalid or has expired. Check your inbox for a new email, or resend below."
            : "We could not verify your email. Please resend the confirmation email.";

        addNotififcation({ type: "info", message });

        if (!cancelled) {
          replaceRoute(routes.setupCheckEmail);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hash, verifySignupEmail, loadSelf, replaceRoute, addNotififcation]);

  return (
    <>
      <Helmet>
        <title>Confirm your email | Deskpass Teams</title>
      </Helmet>

      <LoadingSpinner />
    </>
  );
};

export default ConfirmEmailPage;
